import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Gift, Loader2, Check } from "lucide-react";
import { useAccount } from "@/context/AccountAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

export default function FreeReadingPromo({ onSecondary }) {
  const { user } = useAccount();
  const [form, setForm] = useState({ name: "", email: "", question: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [claimed, setClaimed] = useState(false);

  // Prefill for signed-in clients so the free reading links to their account
  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
      }));
    }
  }, [user]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.email.includes("@")) {
      toast.error("Please add your name and a valid email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/bookings`,
        {
          name: form.name,
          email: form.email,
          service: "Free Trial Reading (Promo)",
          question: form.question.trim() || "Claiming the complimentary free trial reading offer.",
        },
        { withCredentials: true }
      );
      setDone(true);
      toast.success("Your free reading is reserved ✦");
    } catch (err) {
      if (err?.response?.status === 409) {
        setClaimed(true);
        toast(err.response.data?.detail || "You've already claimed your free reading.");
      } else {
        toast.error("Something wavered. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-full bg-[#140B09] border border-[#C9A24B]/25 px-4 py-3 text-sm text-[#EFE3CC] placeholder:text-[#7a6a55] outline-none focus:border-[#C77E8A] focus:ring-1 focus:ring-[#C77E8A]/40 transition-all";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.25, ease }}
      className="mt-8 w-full max-w-md rounded-lg border border-[#C9A24B]/45 bg-gradient-to-b from-[#2a121f] to-[#1a0f10] p-6 shadow-[0_0_50px_rgba(156,42,82,0.25)]"
      data-testid="free-reading-promo"
    >
      <span className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B]/15 border border-[#C9A24B]/40 px-3 py-1 text-[10px] tracking-[0.28em] uppercase text-[#E9D3A0]">
        <Gift className="w-3.5 h-3.5" strokeWidth={1.5} /> Limited · Free Trial
      </span>

      {done ? (
        <div className="mt-5 flex flex-col items-center text-center" data-testid="promo-success">
          <div className="w-12 h-12 rounded-full border border-[#C9A24B]/40 flex items-center justify-center glow-gold mb-3">
            <Check className="w-5 h-5 text-[#C9A24B]" strokeWidth={1.5} />
          </div>
          <p className="font-display italic text-2xl text-[#EFE3CC]">Your free reading is reserved</p>
          <p className="mt-2 text-sm text-[#B7A488] font-light">I&apos;ll reach out personally within 24 hours ✦</p>
        </div>
      ) : claimed ? (
        <div className="mt-5 flex flex-col items-center text-center" data-testid="promo-claimed">
          <div className="w-12 h-12 rounded-full border border-[#C9A24B]/40 flex items-center justify-center mb-3">
            <Gift className="w-5 h-5 text-[#C9A24B]" strokeWidth={1.5} />
          </div>
          <p className="font-display italic text-2xl text-[#EFE3CC]">One free reading per soul ✦</p>
          <p className="mt-2 text-sm text-[#B7A488] font-light">
            It looks like you&apos;ve already claimed your complimentary 5-minute reading. Ready to go deeper?
          </p>
          <a
            href="/pricing"
            data-testid="promo-claimed-book"
            className="mt-5 inline-block rounded-full bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] px-8 py-3 text-xs tracking-[0.2em] uppercase text-[#EFE3CC] shadow-[0_0_30px_rgba(156,42,82,0.35)] transition-transform duration-300 hover:scale-[1.02]"
          >
            Book a Full Reading
          </a>
        </div>
      ) : (
        <>
          <h3 className="mt-4 font-display text-2xl sm:text-3xl text-[#EFE3CC] leading-tight">
            Your first mini-reading is <span className="italic text-gold-gradient">on me</span>
          </h3>
          <p className="mt-2 text-sm text-[#B7A488] font-light">
            Leave your details and I&apos;ll send a complimentary trial reading within 24 hours.
          </p>

          <form onSubmit={submit} className="mt-5 space-y-3" data-testid="promo-form">
            <input type="text" value={form.name} onChange={update("name")} data-testid="promo-name" placeholder="Your name" className={inputCls} />
            <input type="email" value={form.email} onChange={update("email")} data-testid="promo-email" placeholder="your@email.com" className={inputCls} />
            <input type="text" value={form.question} onChange={update("question")} data-testid="promo-question" placeholder="What's on your heart? (optional)" className={inputCls} />
            <button
              type="submit"
              disabled={loading}
              data-testid="promo-submit"
              className="w-full rounded-full bg-gradient-to-r from-[#9C2A52] to-[#C77E8A] py-3.5 text-xs tracking-[0.2em] uppercase text-[#EFE3CC] shadow-[0_0_30px_rgba(156,42,82,0.35)] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" strokeWidth={1.5} />} Claim My Free Reading
            </button>
          </form>
        </>
      )}

      <a
        href="#contact"
        onClick={onSecondary}
        data-testid="promo-full-reading-link"
        className="mt-4 block text-center text-[11px] tracking-[0.15em] uppercase text-[#B7A488] hover:text-[#E9D3A0] transition-colors"
      >
        Prefer a full session? Book a reading →
      </a>
    </motion.div>
  );
}
